import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Modal,
  Form,
  Select,
  Divider,
  Typography,
  Space,
  Button,
  message,
  Card,
  Table,
  Tag,
  Alert,
  Collapse,
  Spin,
  Tabs,
  Descriptions,
} from 'antd';
import {
  BellOutlined,
  MailOutlined,
  MessageOutlined,
  DollarOutlined,
  FileTextOutlined,
  ToolOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { notificationSettingsApi } from '../../services/api';
import { apiRoot } from '../../services/apiInstances';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { TabPane } = Tabs;

/**
 * Notification event grouped by category
 */
interface NotificationEvent {
  key: string;
  label: string;
  description: string;
  defaultMethod: 'off' | 'email' | 'sms' | 'both';
}

interface NotificationCategory {
  title: string;
  icon: React.ReactNode;
  color: string;
  events: NotificationEvent[];
}

/**
 * New Simplified Notification Settings Modal
 *
 * Groups notifications by category with unified Email/SMS controls
 * Removes manual phone entry - uses phone numbers from user profiles
 */
const NotificationSettingsModal: React.FC<{ visible: boolean; onClose: () => void }> = ({
  visible,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notificationMethods, setNotificationMethods] = useState<Record<string, string>>({});
  const [personalPreferences, setPersonalPreferences] = useState<Record<string, string>>({});
  const [userInfo, setUserInfo] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('global');

  // Determine the role prefix from current path
  const getRolePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    if (location.pathname.startsWith('/technician')) return '/technician';
    return '/admin';
  };

  // Grouped notification categories
  const notificationCategories: NotificationCategory[] = [
    {
      title: 'Payment Notifications',
      icon: <DollarOutlined />,
      color: '#52c41a',
      events: [
        {
          key: 'payment_completed',
          label: 'Payment Completed',
          description: 'When a customer completes a payment',
          defaultMethod: 'both',
        },
      ],
    },
    {
      title: 'Estimate Notifications',
      icon: <FileTextOutlined />,
      color: '#1890ff',
      events: [
        {
          key: 'estimate_approved',
          label: 'Estimate Approved',
          description: 'When a customer approves an estimate',
          defaultMethod: 'both',
        },
        {
          key: 'estimate_declined',
          label: 'Estimate Declined',
          description: 'When a customer declines an estimate',
          defaultMethod: 'email',
        },
      ],
    },
    {
      title: 'Work Order Notifications',
      icon: <ToolOutlined />,
      color: '#fa8c16',
      events: [
        {
          key: 'line_item_approved',
          label: 'Line Item Approved',
          description: 'When a customer approves a work order line item',
          defaultMethod: 'both',
        },
        {
          key: 'line_item_declined',
          label: 'Line Item Declined',
          description: 'When a customer declines a work order line item',
          defaultMethod: 'both',
        },
        {
          key: 'all_items_approved',
          label: 'All Items Approved',
          description: 'When a customer approves all pending line items',
          defaultMethod: 'both',
        },
      ],
    },
    {
      title: 'Appointment Notifications',
      icon: <CalendarOutlined />,
      color: '#722ed1',
      events: [
        {
          key: 'appointment_scheduled',
          label: 'Appointment Scheduled',
          description: 'When staff schedules an appointment',
          defaultMethod: 'email',
        },
        {
          key: 'appointment_booked',
          label: 'Appointment Booked Online',
          description: 'When a customer books an appointment online',
          defaultMethod: 'both',
        },
        {
          key: 'appointment_rescheduled',
          label: 'Appointment Rescheduled',
          description: 'When an appointment is rescheduled',
          defaultMethod: 'both',
        },
        {
          key: 'appointment_cancelled',
          label: 'Appointment Cancelled',
          description: 'When an appointment is cancelled',
          defaultMethod: 'both',
        },
        {
          key: 'appointment_deleted',
          label: 'Appointment Deleted',
          description: 'When an appointment is permanently deleted',
          defaultMethod: 'email',
        },
        {
          key: 'technician_changed',
          label: 'Technician Changed',
          description: 'When the assigned technician is changed',
          defaultMethod: 'both',
        },
      ],
    },
    {
      title: 'Quote Request Notifications',
      icon: <QuestionCircleOutlined />,
      color: '#13c2c2',
      events: [
        {
          key: 'quote_request_received',
          label: 'Quote Request Received',
          description: 'When a new quote request is submitted',
          defaultMethod: 'both',
        },
      ],
    },
  ];

  useEffect(() => {
    if (visible) {
      loadSettings();
    }
  }, [visible]);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Load global settings
      const response = await notificationSettingsApi.get();
      const settings = response.data;

      // Build notification methods map from existing settings
      const methods: Record<string, string> = {};

      notificationCategories.forEach(category => {
        category.events.forEach(event => {
          const email = settings[`email_${event.key}`] !== false;
          const sms = settings[`sms_${event.key}`] !== false;

          if (!email && !sms) {
            methods[event.key] = 'off';
          } else if (email && sms) {
            methods[event.key] = 'both';
          } else if (email) {
            methods[event.key] = 'email';
          } else {
            methods[event.key] = 'sms';
          }
        });
      });

      setNotificationMethods(methods);

      // Load personal preferences
      try {
        const tokens = localStorage.getItem('cnf_auth_tokens');
        const accessToken = tokens ? JSON.parse(tokens).accessToken : null;

        if (accessToken) {
          // Load user info
          const userResponse = await apiRoot.get('/users/me/info');
          setUserInfo(userResponse.data);

          // Load personal preferences
          const prefsResponse = await apiRoot.get('/users/me/notification-preferences');

          const personalPrefs: Record<string, string> = {};
          Object.entries(prefsResponse.data).forEach(([key, prefs]: [string, any]) => {
            if (!prefs.email_enabled && !prefs.sms_enabled) {
              personalPrefs[key] = 'off';
            } else if (prefs.email_enabled && prefs.sms_enabled) {
              personalPrefs[key] = 'both';
            } else if (prefs.email_enabled) {
              personalPrefs[key] = 'email';
            } else {
              personalPrefs[key] = 'sms';
            }
          });

          setPersonalPreferences(personalPrefs);
        }
      } catch (personalError) {
        console.log('Could not load personal preferences:', personalError);
        // Not a critical error - user might not have set preferences yet
      }
    } catch (error: any) {
      console.error('Error loading notification settings:', error);
      message.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Convert notification methods back to individual email/sms/realtime flags
      const settings: any = {};

      notificationCategories.forEach(category => {
        category.events.forEach(event => {
          const method = notificationMethods[event.key] || 'off';

          settings[`email_${event.key}`] = method === 'email' || method === 'both';
          settings[`sms_${event.key}`] = method === 'sms' || method === 'both';
          settings[`realtime_${event.key}`] = method !== 'off'; // Real-time always enabled unless off
          settings[`default_method_${event.key}`] = method === 'both' ? 'sms' : method;
        });
      });

      await notificationSettingsApi.update(settings);
      message.success('Notification settings saved successfully');
      onClose();
    } catch (error: any) {
      console.error('Error saving notification settings:', error);
      message.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleMethodChange = (eventKey: string, method: string) => {
    setNotificationMethods(prev => ({
      ...prev,
      [eventKey]: method,
    }));
  };

  const handlePersonalMethodChange = (eventKey: string, method: string) => {
    setPersonalPreferences(prev => ({
      ...prev,
      [eventKey]: method,
    }));
  };

  const handleSavePersonalPreferences = async () => {
    try {
      setSaving(true);

      const tokens = localStorage.getItem('cnf_auth_tokens');
      const accessToken = tokens ? JSON.parse(tokens).accessToken : null;

      if (!accessToken) {
        message.error('Authentication required');
        return;
      }

      // Convert personal preferences to API format
      const preferences: any = {};

      notificationCategories.forEach(category => {
        category.events.forEach(event => {
          const method = personalPreferences[event.key] || 'off';

          preferences[event.key] = {
            email_enabled: method === 'email' || method === 'both',
            sms_enabled: method === 'sms' || method === 'both',
            realtime_enabled: method !== 'off',
            preferred_channel: method,
          };
        });
      });

      await apiRoot.put('/users/me/notification-preferences', preferences);

      message.success('Your notification preferences saved successfully');
      onClose();
    } catch (error: any) {
      console.error('Error saving personal notification preferences:', error);
      message.error('Failed to save your notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const getMethodTag = (method: string) => {
    switch (method) {
      case 'off':
        return <Tag color="default">Off</Tag>;
      case 'email':
        return <Tag color="blue" icon={<MailOutlined />}>Email Only</Tag>;
      case 'sms':
        return <Tag color="green" icon={<MessageOutlined />}>SMS Only</Tag>;
      case 'both':
        return <Tag color="purple">Email + SMS</Tag>;
      default:
        return <Tag color="default">Off</Tag>;
    }
  };

  const getColumns = (isPersonal: boolean) => [
    {
      title: 'Notification Type',
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record: NotificationEvent) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Notification Method',
      dataIndex: 'key',
      key: 'method',
      width: 250,
      render: (key: string) => {
        const methods = isPersonal ? personalPreferences : notificationMethods;
        const handleChange = isPersonal ? handlePersonalMethodChange : handleMethodChange;

        return (
          <Select
            value={methods[key] || 'off'}
            onChange={(value) => handleChange(key, value)}
            style={{ width: '100%' }}
          >
            <Option value="off">
              <Space>
                <Tag color="default">Off</Tag>
                <Text type="secondary">No notifications</Text>
              </Space>
            </Option>
            <Option value="email">
              <Space>
                <MailOutlined />
                <Text>Email Only</Text>
              </Space>
            </Option>
            <Option value="sms">
              <Space>
                <MessageOutlined />
                <Text>SMS Only</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  (~$0.0075/msg)
                </Text>
              </Space>
            </Option>
            <Option value="both">
              <Space>
                <MailOutlined />
                <MessageOutlined />
                <Text>Email + SMS</Text>
              </Space>
            </Option>
          </Select>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <BellOutlined />
          Notification Settings
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      centered
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={activeTab === 'global' ? handleSave : handleSavePersonalPreferences}
        >
          {activeTab === 'global' ? 'Save Global Settings' : 'Save My Preferences'}
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Global Settings Tab */}
          <TabPane tab="Global Settings" key="global">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Info Alert */}
              <Alert
                message="Global Notification Settings"
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      Configure default notification settings for the entire shop. These settings apply to all staff members unless they override them in their personal preferences.
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Who gets notified?</Text> Owners receive notifications based on their personal preferences.
                      Advisors and technicians only receive notifications for work orders they're assigned to.
                    </Paragraph>
                  </div>
                }
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
              />

              {/* Grouped Notification Categories */}
              <Collapse defaultActiveKey={['0']} ghost>
                {notificationCategories.map((category, index) => (
                  <Panel
                    key={index.toString()}
                    header={
                      <Space>
                        <span style={{ color: category.color, fontSize: 18 }}>
                          {category.icon}
                        </span>
                        <Text strong>{category.title}</Text>
                        <Tag color={category.color}>{category.events.length} events</Tag>
                      </Space>
                    }
                  >
                    <Table
                      dataSource={category.events}
                      columns={getColumns(false)}
                      pagination={false}
                      size="small"
                      rowKey="key"
                    />
                  </Panel>
                ))}
              </Collapse>

              {/* SMS Cost Warning */}
              <Alert
                message="SMS Notification Costs"
                description="SMS notifications cost approximately $0.0075 per message. Email notifications are free. Choose 'Email + SMS' for critical notifications only."
                type="warning"
                showIcon
              />

              {/* User Management Link */}
              <Card size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>📱 Phone Number Management</Text>
                  <Text type="secondary">
                    Phone numbers for SMS notifications are now managed in user profiles.
                    Go to Users → [User Profile] → Edit to add/update phone numbers.
                  </Text>
                  <Button
                    type="link"
                    style={{ paddingLeft: 0 }}
                    onClick={() => {
                      const rolePrefix = getRolePrefix();
                      navigate(`${rolePrefix}/users`);
                      onClose();
                    }}
                  >
                    Manage User Profiles →
                  </Button>
                </Space>
              </Card>
            </Space>
          </TabPane>

          {/* Personal Preferences Tab */}
          <TabPane tab="My Preferences" key="personal">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* User Info */}
              {userInfo && (
                <Card size="small">
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Name">
                      {userInfo.firstName} {userInfo.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Role">
                      <Tag color={userInfo.role === 'owner' ? 'red' : userInfo.role === 'advisor' ? 'blue' : 'green'}>
                        {userInfo.role?.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      {userInfo.email}
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      {userInfo.phone || <Text type="secondary">Not set</Text>}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}

              {/* Info Alert */}
              <Alert
                message="Personal Notification Preferences"
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      These are YOUR personal notification preferences. They override the global settings for your account only.
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Note:</Text> You will only receive notifications for work orders and estimates you are assigned to, plus any events you choose to receive as an owner.
                    </Paragraph>
                  </div>
                }
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
              />

              {/* Grouped Notification Categories */}
              <Collapse defaultActiveKey={['0']} ghost>
                {notificationCategories.map((category, index) => (
                  <Panel
                    key={index.toString()}
                    header={
                      <Space>
                        <span style={{ color: category.color, fontSize: 18 }}>
                          {category.icon}
                        </span>
                        <Text strong>{category.title}</Text>
                        <Tag color={category.color}>{category.events.length} events</Tag>
                      </Space>
                    }
                  >
                    <Table
                      dataSource={category.events}
                      columns={getColumns(true)}
                      pagination={false}
                      size="small"
                      rowKey="key"
                    />
                  </Panel>
                ))}
              </Collapse>

              {/* SMS Cost Warning */}
              <Alert
                message="SMS Notification Costs"
                description="SMS notifications cost approximately $0.0075 per message. Email notifications are free. Choose 'Email + SMS' for critical notifications only."
                type="warning"
                showIcon
              />

              {/* Phone Number Update Link */}
              {!userInfo?.phone && (
                <Alert
                  message="Phone Number Required for SMS"
                  description={
                    <div>
                      <Paragraph style={{ marginBottom: 8 }}>
                        You need to add a phone number to your profile to receive SMS notifications.
                      </Paragraph>
                      <Button
                        type="link"
                        style={{ paddingLeft: 0 }}
                        onClick={() => {
                          const rolePrefix = getRolePrefix();
                          navigate(`${rolePrefix}/users`);
                          onClose();
                        }}
                      >
                        Update My Profile →
                      </Button>
                    </div>
                  }
                  type="warning"
                  showIcon
                />
              )}
            </Space>
          </TabPane>
        </Tabs>
      </Spin>
    </Modal>
  );
};

export default NotificationSettingsModal;
